import type { LeadSourceBatch } from './leadSourceBatches';

/**
 * Which bulk-fill notices are still on screen, and which have gone quiet.
 *
 * Kyu, 2026-08-26: "show the undo notice immediately after the action · keep
 * it available for 30 seconds · then auto-dismiss it · keep the stack compact
 * while multiple notices are active."
 *
 * ── The distinction this module exists to protect ──────────────────────────
 *
 * These are NOT toasts. The strip is a query — the last five bulk-fill batches
 * that have not been reverted — and it is the ONLY route to
 * `revert_lead_source_batch` anywhere in the app. Nothing else can undo a bulk
 * fill.
 *
 * So "auto-dismiss" had to mean dismissing the NOTICE, not the undo. A batch
 * that scrolls past thirty seconds must still be reachable, or the fix for
 * "Nez marked 212 leads Outbound yesterday and it was wrong" stops being one
 * click and becomes 212 admin corrections with a typed reason each.
 *
 * Kyu chose exactly that when the fork was put to him: hide the notice, keep
 * the way back.
 */

/** Kyu's number. One place, so the notice and its test cannot disagree. */
export const NOTICE_WINDOW_MS = 30_000;

/**
 * How many notices stay expanded at once before the rest stack behind a count.
 *
 * Two, not one. Working the Needs Review queue means confirming several groups
 * in a row, and collapsing the moment a second appears would hide the undo for
 * the fill somebody made four seconds ago — the exact moment they are most
 * likely to want it.
 */
export const NOTICE_STACK_LIMIT = 2;

export interface BatchNotices {
  /** Fresh enough to show as a notice, newest first. */
  visible: LeadSourceBatch[];
  /** Fresh, but beyond the stack limit. Reachable behind the count. */
  overflow: LeadSourceBatch[];
  /** Past the window. Reachable behind "Recent bulk fills". */
  older: LeadSourceBatch[];
}

/**
 * Split the batches by age.
 *
 * `now` is passed in rather than read here so this is a pure function and the
 * tests do not have to fake a clock to say what happens at 29 and 31 seconds.
 */
export function splitBatchNotices(
  batches: LeadSourceBatch[],
  now: number,
  windowMs: number = NOTICE_WINDOW_MS,
  stackLimit: number = NOTICE_STACK_LIMIT,
): BatchNotices {
  const fresh: LeadSourceBatch[] = [];
  const older: LeadSourceBatch[] = [];

  for (const batch of batches) {
    const age = now - Date.parse(batch.created_at);
    // A timestamp that will not parse counts as OLD, not fresh. Fresh is the
    // louder state, and a clock skew or a bad row should not pin a notice to
    // the screen with no way to make it go away.
    if (Number.isFinite(age) && age >= 0 && age < windowMs) {
      fresh.push(batch);
    } else {
      older.push(batch);
    }
  }

  // Newest first within the fresh group, so a burst of fills reads top-down in
  // the order somebody made them.
  fresh.sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));

  return {
    visible: fresh.slice(0, stackLimit),
    overflow: fresh.slice(stackLimit),
    older,
  };
}

/**
 * Milliseconds until the oldest visible notice expires, or null when nothing
 * is counting down.
 *
 * The strip needs a re-render at the moment a notice ages out; without this it
 * would sit there until some other state change happened to repaint the page,
 * which on a screen somebody is reading is most of the time. One timer for the
 * next expiry, rather than a one-second interval that runs all day.
 */
export function msUntilNextExpiry(
  batches: LeadSourceBatch[],
  now: number,
  windowMs: number = NOTICE_WINDOW_MS,
): number | null {
  const remaining = batches
    .map((batch) => Date.parse(batch.created_at) + windowMs - now)
    .filter((ms) => Number.isFinite(ms) && ms > 0);
  if (remaining.length === 0) {
    return null;
  }
  return Math.min(...remaining);
}

/** "3 recent bulk fills" for the control that keeps the undo reachable. */
export function olderBatchesLabel(count: number): string {
  return `${String(count)} recent bulk fill${count === 1 ? '' : 's'}`;
}
